import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { VerificationGate } from "@/components/VerificationGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, CheckCircle2, ListChecks, Wallet, Clock, Banknote, Trophy, Copy, Loader2, Share2 } from "lucide-react";

const money = (n) => `R${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };
const STATUS_CLS = { paid: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700", registered: "bg-slate-100 text-slate-500" };
const STATUS_LABEL = { paid: "Paid", pending: "Pending", registered: "Registered" };

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid={`inf-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        <span className={`w-9 h-9 rounded-md flex items-center justify-center ${accent || "bg-[#0B2447]/5"}`}><Icon className="w-4 h-4 text-[#0B2447]" /></span>
      </div>
      <div className="font-display text-3xl font-bold text-[#0B2447] mt-3">{value}</div>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  useEffect(() => {
    api.get("/influencer/dashboard").then(({ data }) => setData(data)).catch(() => toast.error("Could not load your dashboard."));
    api.get("/influencer/leaderboard").then(({ data }) => setBoard(data.leaderboard)).catch(() => {});
  }, []);
  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>;

  const { profile, stats, referrals, commission_rates } = data;
  const refLink = `${window.location.origin}/register?ref=${profile.ref_code}`;
  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied!"); };
  const medal = ["🥇", "🥈", "🥉"];

  return (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Influencer</p>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1" data-testid="inf-title">Welcome, {profile.full_name || profile.email}</h1>
      <p className="text-slate-600 mt-2">Earn <span className="font-semibold text-[#0B2447]">{money(commission_rates.unlock)}</span> for every referral who pays the unlock fee, plus <span className="font-semibold text-[#0B2447]">{money(commission_rates.application)}</span> for every university application they pay for.</p>

      <div className="mt-6 bg-white border border-[#0B2447]/20 rounded-xl p-5 shadow-sm" data-testid="inf-link-card">
        <div className="flex items-center gap-2 text-[#0B2447]"><Share2 className="w-4 h-4" /><span className="font-display font-bold">Your referral link</span></div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <code className="flex-1 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 text-sm text-slate-700 break-all" data-testid="inf-ref-link">{refLink}</code>
          <Button onClick={copy} data-testid="inf-copy-link" className="bg-[#0B2447] hover:bg-[#153461]"><Copy className="w-4 h-4 mr-1.5" /> Copy link</Button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Share this link. Anyone who registers through it is automatically tracked to you.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="inf-stats">
        <StatCard icon={Users} label="Total Referrals" value={stats.total_referrals} sub="Registered via your link" />
        <StatCard icon={CheckCircle2} label="Qualified Referrals" value={stats.qualified_referrals} sub="Paid the unlock fee" accent="bg-emerald-100" />
        <StatCard icon={ListChecks} label="University Applications" value={stats.applications} sub="Paid applications" accent="bg-[#F5C32C]/20" />
        <StatCard icon={Wallet} label="Total Earnings" value={money(stats.earned)} sub="Commission earned" />
        <StatCard icon={Clock} label="Pending Earnings" value={money(stats.pending)} sub="Awaiting payout" accent="bg-amber-100" />
        <StatCard icon={Banknote} label="Paid Earnings" value={money(stats.paid)} sub="Already paid out" accent="bg-emerald-100" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="inf-history">
          <div className="p-4 border-b border-slate-100"><h3 className="font-display font-bold text-[#0B2447]">Referral history</h3></div>
          <Table>
            <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Registered</TableHead><TableHead className="text-center">Qualified</TableHead><TableHead className="text-center">Apps</TableHead><TableHead className="text-center">Earned</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {referrals.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No referrals yet — share your link to get started!</TableCell></TableRow>}
              {referrals.map((r, i) => (
                <TableRow key={i} data-testid={`inf-ref-row-${i}`}>
                  <TableCell><div className="font-medium text-[#0B2447]">{r.student_name || "—"}</div><div className="text-xs text-slate-400">{r.student_email}</div></TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDate(r.registered_at)}</TableCell>
                  <TableCell className="text-center">{r.qualified ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-center font-medium text-[#0B2447]">{r.applications}</TableCell>
                  <TableCell className="text-center font-semibold text-[#0B2447]">{money(r.earned)}</TableCell>
                  <TableCell><Badge className={`${STATUS_CLS[r.status]} border-0`}>{STATUS_LABEL[r.status]}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="inf-leaderboard">
          <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-[#F5C32C]" /><h3 className="font-display font-bold text-[#0B2447]">Influencer Leaderboard</h3></div>
          <div className="mt-4 space-y-1.5">
            {board.length === 0 && <p className="text-sm text-slate-400">No rankings yet.</p>}
            {board.map((r, i) => {
              const isMe = r.name === (profile.full_name || profile.email);
              return (
                <div key={i} data-testid={`inf-lb-row-${i}`} className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${isMe ? "bg-[#0B2447]/5 border border-[#0B2447]/20" : "bg-slate-50"}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-6 text-center">{i < 3 ? medal[i] : <span className="text-slate-400">{i + 1}</span>}</span>
                    <span className="font-medium text-[#0B2447]">{r.name}{isMe ? " (you)" : ""}</span>
                  </div>
                  <div className="text-xs text-slate-500 text-right"><span className="font-bold text-[#0B2447]">{r.qualified_referrals}</span> qualified · {money(r.earned)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default function Influencer() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <VerificationGate><Dashboard /></VerificationGate>
      </div>
    </div>
  );
}
